import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Building2, Search, Plus, Eye, Edit, Trash2, 
  Phone, Mail, MapPin, TrendingUp, DollarSign, FileText,
  X, Star, Bell, AlertCircle, MessageSquare, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRM = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({ total_count: 0, high_priority_count: 0 });

  // Customer messaging state
  const [customerMessages, setCustomerMessages] = useState([]);
  const [newCustomerMessage, setNewCustomerMessage] = useState('');
  const [messageType, setMessageType] = useState('comment');
  const [messagePriority, setMessagePriority] = useState('normal');
  const [sendingMessage, setSendingMessage] = useState(false);

  const [newSupplier, setNewSupplier] = useState({
    company_name: '',
    supplier_type: 'Materials',
    contact_person: '',
    contact_email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    payment_terms: '',
    products_services: '',
    status: 'Active',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersRes, suppliersRes, statsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/crm/customers`),
        axios.get(`${API}/crm/suppliers`),
        axios.get(`${API}/crm/statistics`),
        axios.get(`${API}/messages/pending-actions`).catch(() => ({ data: { all_messages: [], total_count: 0, high_priority_count: 0 } }))
      ]);
      
      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setPendingMessages(messagesRes.data.all_messages || []);
      setMessageStats({
        total_count: messagesRes.data.total_count || 0,
        high_priority_count: messagesRes.data.high_priority_count || 0
      });
      setStatistics(statsRes.data);
    } catch (error) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetail = async (customerId) => {
    try {
      const response = await axios.get(`${API}/crm/customers/${customerId}`);
      setSelectedCustomer(response.data);
      setShowCustomerDetail(true);
      // Load customer messages
      await loadCustomerMessages(customerId);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const loadCustomerMessages = async (customerId) => {
    try {
      const response = await axios.get(`${API}/crm/customers/${customerId}/messages`);
      setCustomerMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load customer messages:', error);
      setCustomerMessages([]);
    }
  };

  const handleSendCustomerMessage = async () => {
    if (!newCustomerMessage.trim() || !selectedCustomer) return;
    
    setSendingMessage(true);
    try {
      const messageData = {
        message: newCustomerMessage,
        message_type: messageType,
        priority: messagePriority,
        is_customer_message: false // Staff sending to customer
      };

      await axios.post(`${API}/crm/customers/${selectedCustomer.user_id}/messages`, messageData);
      
      toast.success('Message sent successfully');
      setNewCustomerMessage('');
      setMessageType('comment');
      setMessagePriority('normal');
      
      // Reload messages
      await loadCustomerMessages(selectedCustomer.user_id);
    } catch (error) {
      toast.error('Failed to send message');
      console.error('Send message error:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/suppliers`, newSupplier);
      toast.success('Supplier created successfully!');
      setShowSupplierForm(false);
      setNewSupplier({
        company_name: '', supplier_type: 'Materials', contact_person: '',
        contact_email: '', phone: '', address: '', city: '', state: '',
        zip_code: '', website: '', payment_terms: '', products_services: '',
        status: 'Active', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create supplier');
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setNewSupplier(supplier);
    setShowSupplierForm(true);
  };

  const handleUpdateSupplier = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/crm/suppliers/${editingSupplier.id}`, newSupplier);
      toast.success('Supplier updated successfully!');
      setShowSupplierForm(false);
      setEditingSupplier(null);
      setNewSupplier({
        company_name: '', supplier_type: 'Materials', contact_person: '',
        contact_email: '', phone: '', address: '', city: '', state: '',
        zip_code: '', website: '', payment_terms: '', products_services: '',
        status: 'Active', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to update supplier');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    
    try {
      await axios.delete(`${API}/crm/suppliers/${supplierId}`);
      toast.success('Supplier deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchTerm || 
      customer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesIndustry = !industryFilter || customer.industry === industryFilter;
    
    return matchesSearch && matchesIndustry;
  });

  const filteredSuppliers = suppliers.filter(supplier =>
    !searchTerm || 
    supplier.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          CRM
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Customer & Supplier Relationship Management
        </p>
      </div>

      {/* Immediate Actions Banner */}
      {messageStats.high_priority_count > 0 && (
        <div className="mb-6">
          <Card className="border-red-500/50 bg-red-50/50 dark:bg-red-950/20 shadow-lg shadow-red-500/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="text-red-500 animate-pulse" size={24} />
                  <div>
                    <h3 className="font-bold text-lg text-red-700 dark:text-red-400">
                      {messageStats.high_priority_count} URGENT Customer Message{messageStats.high_priority_count > 1 ? 's' : ''} Pending!
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      High-priority customer inquiries require immediate attention
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="destructive"
                  className="font-mono uppercase tracking-wider"
                >
                  <AlertCircle className="mr-2" size={16} />
                  View Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Messages Summary */}
      {messageStats.total_count > 0 && messageStats.high_priority_count === 0 && (
        <div className="mb-6">
          <Card className="border-blue-500/30 bg-blue-50/30 dark:bg-blue-950/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="text-blue-500" size={20} />
                  <div>
                    <h3 className="font-semibold text-blue-700 dark:text-blue-400">
                      {messageStats.total_count} Pending Customer Message{messageStats.total_count > 1 ? 's' : ''}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Customer inquiries waiting for response
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  size="sm"
                  className="font-mono uppercase tracking-wider text-xs"
                >
                  View All
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border">
            <CardContent className="p-6">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                Total Customers
              </p>
              <p className="text-3xl font-bold font-mono text-primary">{statistics.customers.total}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {statistics.customers.completion_rate.toFixed(0)}% profiles complete
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-6">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                Total Suppliers
              </p>
              <p className="text-3xl font-bold font-mono text-blue-500">{statistics.suppliers.total}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {statistics.suppliers.active} active
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-6">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                Preferred Suppliers
              </p>
              <p className="text-3xl font-bold font-mono text-green-500">{statistics.suppliers.preferred}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-6">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                Top Industry
              </p>
              <p className="text-2xl font-bold font-mono text-foreground">
                {statistics.industries[0]?._id || 'N/A'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {statistics.industries[0]?.count || 0} customers
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search by company, name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          className="px-4 py-2 bg-background border border-input rounded-sm font-mono text-sm"
        >
          <option value="">All Industries</option>
          <option value="Construction">Construction</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Agriculture">Agriculture</option>
          <option value="Automotive">Automotive</option>
          <option value="Energy">Energy</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="customers" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="customers" className="font-mono uppercase">
            <Users size={16} className="mr-2" />
            Customers ({filteredCustomers.length})
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="font-mono uppercase">
            <Building2 size={16} className="mr-2" />
            Suppliers ({filteredSuppliers.length})
          </TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Customer Directory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Company</th>
                      <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Contact</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Industry</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Type</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Frequency</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                          No customers found
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <tr key={customer.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-semibold">{customer.company_name}</p>
                              <p className="text-xs text-muted-foreground">{customer.city}, {customer.state}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{customer.contact_person}</p>
                              <p className="text-xs text-muted-foreground font-mono">{customer.contact_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-mono">
                              {customer.industry}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center text-xs">{customer.business_type}</td>
                          <td className="py-3 px-2 text-center text-xs">{customer.project_frequency}</td>
                          <td className="py-3 px-2 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => loadCustomerDetail(customer.user_id)}
                              className="text-primary hover:text-primary/80"
                            >
                              <Eye size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers">
          <div className="mb-4">
            <Button
              onClick={() => setShowSupplierForm(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase"
            >
              <Plus size={16} className="mr-2" />
              Add Supplier
            </Button>
          </div>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Supplier Directory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Company</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Contact</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Terms</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Status</th>
                      <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuppliers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                          No suppliers found
                        </td>
                      </tr>
                    ) : (
                      filteredSuppliers.map((supplier) => (
                        <tr key={supplier.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-semibold">{supplier.company_name}</p>
                              <p className="text-xs text-muted-foreground">{supplier.city}, {supplier.state}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-mono">
                              {supplier.supplier_type}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{supplier.contact_person}</p>
                              <p className="text-xs text-muted-foreground font-mono">{supplier.contact_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center text-xs">{supplier.payment_terms || 'N/A'}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-mono ${
                              supplier.status === 'Preferred' 
                                ? 'bg-green-500/20 text-green-400' 
                                : supplier.status === 'Active'
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {supplier.status}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditSupplier(supplier)}
                                className="text-blue-500 hover:text-blue-400"
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteSupplier(supplier.id)}
                                className="text-red-500 hover:text-red-400"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Customer Detail Modal */}
      {showCustomerDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-sm max-w-4xl w-full my-8">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-black tracking-tight uppercase text-foreground">
                    {selectedCustomer.profile.company_name}
                  </h2>
                  <p className="text-sm font-mono text-muted-foreground mt-1">
                    {selectedCustomer.profile.industry} • {selectedCustomer.profile.business_type}
                  </p>
                </div>
                <button 
                  onClick={() => setShowCustomerDetail(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Quotes</p>
                    <p className="text-2xl font-bold font-mono text-blue-500">
                      {selectedCustomer.statistics.total_quotes}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ${selectedCustomer.statistics.total_quotes_value.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Invoices</p>
                    <p className="text-2xl font-bold font-mono text-primary">
                      {selectedCustomer.statistics.total_invoices}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      ${selectedCustomer.statistics.total_invoiced.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Paid</p>
                    <p className="text-2xl font-bold font-mono text-green-500">
                      ${selectedCustomer.statistics.total_paid.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Outstanding</p>
                    <p className="text-2xl font-bold font-mono text-red-500">
                      ${selectedCustomer.statistics.outstanding_balance.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Info */}
              <Card className="border-border mb-4">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold uppercase">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Users size={18} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Contact Person</p>
                      <p className="font-medium">{selectedCustomer.profile.contact_person}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-mono text-sm">{selectedCustomer.profile.contact_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-mono">{selectedCustomer.profile.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin size={18} className="text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Location</p>
                      <p>{selectedCustomer.profile.city}, {selectedCustomer.profile.state}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Messages Section */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg font-semibold uppercase">
                    <MessageSquare className="text-primary" size={20} />
                    Customer Communication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Message Input */}
                  <div className="mb-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-mono uppercase">Message Type</Label>
                        <select
                          value={messageType}
                          onChange={(e) => setMessageType(e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-input rounded-sm text-sm"
                        >
                          <option value="comment">General Comment</option>
                          <option value="question">Question</option>
                          <option value="update">Project Update</option>
                          <option value="invoice">Invoice Related</option>
                          <option value="quote">Quote Related</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase">Priority</Label>
                        <select
                          value={messagePriority}
                          onChange={(e) => setMessagePriority(e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-input rounded-sm text-sm"
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs font-mono uppercase">Message</Label>
                      <Textarea
                        value={newCustomerMessage}
                        onChange={(e) => setNewCustomerMessage(e.target.value)}
                        placeholder="Type your message to the customer..."
                        className="min-h-[80px] mt-1"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleSendCustomerMessage}
                      disabled={!newCustomerMessage.trim() || sendingMessage}
                      className="w-full"
                    >
                      <Send size={16} className="mr-2" />
                      {sendingMessage ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>

                  {/* Messages List */}
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-mono uppercase text-muted-foreground mb-4">
                      Message History ({customerMessages.length})
                    </h3>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {customerMessages.length > 0 ? (
                        customerMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`p-4 rounded-sm border ${
                              msg.is_customer_message 
                                ? 'bg-accent/50 border-accent' 
                                : 'bg-primary/5 border-primary/20'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant={msg.is_customer_message ? "secondary" : "default"}>
                                  {msg.is_customer_message ? 'Customer' : 'Staff'}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {msg.message_type}
                                </Badge>
                                {msg.priority !== 'normal' && (
                                  <Badge 
                                    variant={msg.priority === 'urgent' ? 'destructive' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {msg.priority}
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground font-mono">
                                {new Date(msg.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm">{msg.message}</p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12 text-muted-foreground">
                          <MessageSquare className="mx-auto mb-3 opacity-20" size={48} />
                          <p className="text-sm">No messages yet</p>
                          <p className="text-xs mt-1">Start a conversation by sending a message above</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold uppercase">Recent Quotes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedCustomer.quotes.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No quotes yet</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedCustomer.quotes.slice(0, 3).map(quote => (
                          <div key={quote.id} className="flex justify-between items-center text-sm">
                            <span className="font-mono">{quote.quote_number}</span>
                            <span className="text-primary">${quote.total?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold uppercase">Recent Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedCustomer.invoices.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No invoices yet</p>
                    ) : (
                      <div className="space-y-2">
                        {selectedCustomer.invoices.slice(0, 3).map(invoice => (
                          <div key={invoice.id} className="flex justify-between items-center text-sm">
                            <span className="font-mono">{invoice.invoice_number}</span>
                            <span className={invoice.status === 'Paid' ? 'text-green-500' : 'text-yellow-500'}>
                              {invoice.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Form Modal */}
      {showSupplierForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-sm max-w-2xl w-full my-8">
            <div className="p-6">
              <h2 className="text-2xl font-black tracking-tight uppercase text-foreground mb-6">
                {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
              </h2>

              <form onSubmit={editingSupplier ? handleUpdateSupplier : handleCreateSupplier} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={newSupplier.company_name}
                      onChange={(e) => setNewSupplier({...newSupplier, company_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Supplier Type *</Label>
                    <select
                      value={newSupplier.supplier_type}
                      onChange={(e) => setNewSupplier({...newSupplier, supplier_type: e.target.value})}
                      className="w-full px-3 py-2 bg-background border border-input rounded-sm"
                      required
                    >
                      <option value="Materials">Materials</option>
                      <option value="Tools">Tools</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Services">Services</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Person *</Label>
                    <Input
                      value={newSupplier.contact_person}
                      onChange={(e) => setNewSupplier({...newSupplier, contact_person: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newSupplier.contact_email}
                      onChange={(e) => setNewSupplier({...newSupplier, contact_email: e.target.value})}
                      className="font-mono"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newSupplier.phone}
                      onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={newSupplier.website}
                      onChange={(e) => setNewSupplier({...newSupplier, website: e.target.value})}
                      placeholder="https://"
                      className="font-mono"
                    />
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={newSupplier.address}
                    onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={newSupplier.city}
                      onChange={(e) => setNewSupplier({...newSupplier, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={newSupplier.state}
                      onChange={(e) => setNewSupplier({...newSupplier, state: e.target.value})}
                      maxLength={2}
                      className="uppercase"
                    />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input
                      value={newSupplier.zip_code}
                      onChange={(e) => setNewSupplier({...newSupplier, zip_code: e.target.value})}
                      className="font-mono"
                    />
                  </div>
                </div>

                <div>
                  <Label>Payment Terms</Label>
                  <Input
                    value={newSupplier.payment_terms}
                    onChange={(e) => setNewSupplier({...newSupplier, payment_terms: e.target.value})}
                    placeholder="Net 30, COD, etc."
                  />
                </div>

                <div>
                  <Label>Products/Services</Label>
                  <Textarea
                    value={newSupplier.products_services}
                    onChange={(e) => setNewSupplier({...newSupplier, products_services: e.target.value})}
                    placeholder="What does this supplier provide?"
                    className="min-h-[80px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 font-mono uppercase">
                    Create Supplier
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSupplierForm(false)}
                    className="font-mono uppercase"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRM;
